/**
 * One image slot: preview, Upload / paste-a-URL / Remove, caption, attribution.
 *
 * Hoisted out of {@link TimelineEventModal} when the history hero image needed
 * the same control in the section header. Two callers, one implementation —
 * the same reasoning that put `TimelineSection` itself in this directory.
 *
 * IT NEVER DELETES BYTES, and that is deliberate. The two callers have opposite
 * lifecycles: the event modal holds an unsaved draft, so a Remove there can drop
 * the file immediately; the hero writes on change, so it must persist the
 * shorter document FIRST and only then delete, or a failed write strands the
 * document on a missing file. One component owning both policies would have to
 * compromise one of them, so it owns neither and reports the change instead.
 * Each caller knows its own previous value and decides what to do with the
 * bytes.
 */

import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import type { TimelineEventImage } from '@watchkeeper/shared-types';
import {
  CALIBRE_IMAGE_ATTRIBUTION_SOURCES,
  fetchImageBlob,
  getImageUrl,
} from '../../services/firebase';
import { labelForCalibreSource } from '../../services/calibreSourceLabels';
import { withoutUndefined } from '../../utils/withoutUndefined';

interface Props {
  value?: TimelineEventImage;
  /** `undefined` means the curator removed it. */
  onChange: (next: TimelineEventImage | undefined) => void;
  /** Uploads bytes for this owner and returns the storage path. */
  uploadImage: (file: File | Blob, fileName?: string) => Promise<string>;
  disabled?: boolean;
  /**
   * `square` for an event thumbnail, `banner` for a hero — a hero leads a
   * screen, and a square preview of it misrepresents the crop.
   */
  previewShape?: 'square' | 'banner';
  /** Accessible name for the file input, since two slots can share a screen. */
  label?: string;
  /**
   * Raised while bytes are in flight. The caller uses it to block a Save that
   * would otherwise write the document before the upload had patched it in —
   * losing the image and orphaning the file it had just written.
   */
  onUploadingChange?: (uploading: boolean) => void;
}

export function TimelineImageSlot({
  value,
  onChange,
  uploadImage,
  disabled = false,
  previewShape = 'square',
  label = 'image',
  onUploadingChange,
}: Props) {
  const [uploading, setUploading] = useState(false);

  const setBusy = (busy: boolean) => {
    setUploading(busy);
    onUploadingChange?.(busy);
  };
  const [preview, setPreview] = useState<string | null>(null);
  const [pasteUrl, setPasteUrl] = useState('');

  // Storage paths are not renderable, and a tokenless URL under a non-public
  // prefix 403s — re-sign for display.
  useEffect(() => {
    let cancelled = false;
    if (value?.storagePath) {
      void getImageUrl(value.storagePath)
        .then((url) => !cancelled && setPreview(url))
        .catch(() => !cancelled && setPreview(null));
    } else {
      setPreview(value?.url ?? null);
    }
    return () => {
      cancelled = true;
    };
  }, [value?.storagePath, value?.url]);

  /**
   * Merge `fields` into the current value, DROPPING every key the merge leaves
   * undefined.
   *
   * `undefined` is how this slot says "that key no longer applies" — attaching
   * bytes clears a previously pasted `url`. Firestore rejects undefined inside
   * a map, though, and the owners' `stripUndefined` is shallow: it sweeps the
   * patch's own keys, not the ones nested in the image, so
   * `{ historyHeroImage: { url: undefined } }` reached `updateDoc` and threw
   * "Unsupported field value: undefined (found in field historyHeroImage.url)".
   *
   * Removing the key says the same thing to the writes this feeds. Both of them
   * replace the whole image — the hero writes the map, the event modal writes
   * the events array — so a key that is absent from the object is absent from
   * the document. This is the nested counterpart of the top-level rule the
   * owners already follow: `null` to clear a FIELD, key-absent to clear a key
   * INSIDE one.
   */
  const patch = (fields: Partial<TimelineEventImage>) =>
    onChange(withoutUndefined({ source: 'watchkeeper', ...(value ?? {}), ...fields }));

  /**
   * Attach a file, or fetch a pasted URL's bytes and attach those.
   *
   * The pasted-URL path copies the BYTES rather than storing the foreign
   * address, so the image survives its source going away.
   */
  const attach = async (input: File | string) => {
    if (uploading) return;
    setBusy(true);
    try {
      const blob = typeof input === 'string' ? await fetchImageBlob(input) : input;
      const name =
        typeof input === 'string'
          ? input.split('/').pop()?.split('?')[0] || 'timeline.jpg'
          : input.name;
      const storagePath = await uploadImage(blob, name);
      patch({ storagePath, url: undefined });
    } catch (err) {
      console.error('Timeline image upload failed:', err);
      toast.error('Could not attach that image.');
    } finally {
      setBusy(false);
    }
  };

  const has = Boolean(value?.storagePath || value?.url);
  const frame = previewShape === 'banner' ? 'w-40 h-20' : 'w-24 h-24';

  return (
    <div className="flex items-start gap-3">
      <div
        className={`${frame} shrink-0 rounded bg-white border border-gray-200 overflow-hidden flex items-center justify-center`}
      >
        {preview ? (
          <img src={preview} alt="" className="w-full h-full object-cover" />
        ) : (
          <span className="text-[10px] text-gray-300 text-center px-1">
            {uploading ? 'Uploading…' : 'No image'}
          </span>
        )}
      </div>
      <div className="min-w-0 flex-1 space-y-2">
        {value?.aiSuggestion && !has && (
          <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
            <span className="font-medium">Needs an image:</span> {value.aiSuggestion}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-xs text-indigo-600 hover:underline cursor-pointer">
            Upload
            <input
              type="file"
              accept="image/*"
              aria-label={`Upload ${label}`}
              className="hidden"
              disabled={disabled || uploading}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void attach(file);
                e.target.value = '';
              }}
            />
          </label>
          <span className="text-gray-300 text-xs">·</span>
          <input
            type="url"
            value={pasteUrl}
            aria-label={`Paste a URL for the ${label}`}
            disabled={disabled || uploading}
            onChange={(e) => setPasteUrl(e.target.value)}
            placeholder="paste an image URL"
            className="border border-gray-300 rounded px-2 py-0.5 text-xs flex-1 min-w-[10rem]"
          />
          <button
            type="button"
            className="text-xs text-indigo-600 hover:underline disabled:text-gray-300"
            disabled={disabled || uploading || !pasteUrl.trim()}
            onClick={() => {
              void attach(pasteUrl.trim());
              setPasteUrl('');
            }}
          >
            Fetch
          </button>
          {has && (
            <button
              type="button"
              className="text-xs text-red-600 hover:underline disabled:text-gray-300"
              disabled={disabled || uploading}
              onClick={() => onChange(undefined)}
            >
              Remove
            </button>
          )}
        </div>
        {has && (
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="text"
              value={value?.caption ?? ''}
              aria-label={`Caption for the ${label}`}
              disabled={disabled}
              onChange={(e) => patch({ caption: e.target.value })}
              placeholder="Caption"
              className="border border-gray-300 rounded px-2 py-0.5 text-xs flex-1 min-w-[8rem]"
            />
            <select
              value={value?.source || 'watchkeeper'}
              aria-label={`Attribution source for the ${label}`}
              disabled={disabled}
              onChange={(e) => patch({ source: e.target.value })}
              className="border border-gray-300 rounded px-2 py-0.5 text-xs bg-white"
              title="Image attribution source"
            >
              {CALIBRE_IMAGE_ATTRIBUTION_SOURCES.map((s) => (
                <option key={s} value={s}>
                  {labelForCalibreSource(s)}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );
}
